import { useSyncExternalStore } from "react";

// Cookie consent for tracking (Meta Pixel; PostHog).
//
// Two consumers react to the stored choice:
//   - Meta Pixel (components/analytics/MetaPixel.tsx): loads only after "accepted".
//   - PostHog (lib/analytics/posthogConsent.ts): runs *cookieless* until "accepted" —
//     in-memory persistence, nothing written to the device, session replay off. Accepting
//     upgrades it to persistent storage + replay; withdrawing drops it back and deletes
//     what was stored. Server-side PostHog (instrumentation.ts) is deliberately outside
//     this gate — it stores nothing on the device.
//
// Stored in localStorage (not a cookie) so reading it never touches the network and the
// proxy Route Handler never needs to see it.

export type ConsentValue = "accepted" | "rejected";

export const CONSENT_STORAGE_KEY = "bsa_cookie_consent";

/** Fired on `window` after `setConsent()` writes, so in-tab listeners (the Pixel loader,
 * the banner itself) can react without a reload. Not a storage event — those only fire in
 * *other* tabs, never the tab that made the write. */
const CONSENT_CHANGE_EVENT = "bsa-consent-change";

function isConsentValue(value: string | null): value is ConsentValue {
  return value === "accepted" || value === "rejected";
}

/** SSR-safe: returns null on the server, when nothing has been chosen yet, and when
 * localStorage throws (e.g. Safari private mode). */
export function readConsent(): ConsentValue | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return isConsentValue(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function setConsent(value: ConsentValue): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, value);
  } catch {
    // Private-mode Safari or storage disabled — nothing we can persist; the banner will
    // simply reappear next visit, which is an acceptable degradation.
  }
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT));
}

/** Subscribe to consent changes from anywhere — framework-free, usable outside React
 * (e.g. PostHogInit's setupPosthogConsent wiring, components/analytics/PostHogInit.tsx).
 *
 * Fires on the in-tab `bsa-consent-change` event and on cross-tab `storage` events for the
 * consent key (`key === null` covers `localStorage.clear()`). Storage events never fire in
 * the tab that wrote, so the two listeners are disjoint — no double-fire. */
export function onConsentChange(callback: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === CONSENT_STORAGE_KEY || event.key === null) callback();
  };
  window.addEventListener(CONSENT_CHANGE_EVENT, callback);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(CONSENT_CHANGE_EVENT, callback);
    window.removeEventListener("storage", onStorage);
  };
}

function subscribe(callback: () => void): () => void {
  return onConsentChange(callback);
}

/** Returns null on the server and until the client has read the stored value once, so the
 * first client render matches SSR (no hydration mismatch) before settling on the real value. */
function getConsentSnapshot(): ConsentValue | null {
  return readConsent();
}

function getConsentServerSnapshot(): ConsentValue | null {
  return null;
}

/** Withdraws any stored choice so the banner reappears — the privacy page's "cookie
 * settings" control (a Reject/Accept banner with no way to change your mind
 * later doesn't meet consent-withdrawal expectations). */
export function clearConsent(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // Nothing to clean up if it never persisted in the first place.
  }
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT));
}

/** Reactive read of the stored consent choice. Returns null until the client has hydrated
 * and read localStorage once, and again whenever `setConsent()` is called anywhere in the tab. */
export function useConsent(): ConsentValue | null {
  return useSyncExternalStore(subscribe, getConsentSnapshot, getConsentServerSnapshot);
}

function subscribeNever(): () => void {
  return () => {};
}

function getHydratedTrue(): boolean {
  return true;
}

function getHydratedFalseOnServer(): boolean {
  return false;
}

/** True once the client has hydrated, false during SSR and the first client render.
 *
 * Consumers gate UI that must never appear when `useConsent()`'s answer is already known:
 * `useConsent()` correctly returns null for that first render too — that's
 * required so hydration doesn't mismatch — but a visitor who already chose months ago would
 * otherwise see consent-gated UI flash into the DOM before flipping back out once the real
 * stored value is read on the next tick.
 *
 * Implemented via `useSyncExternalStore` with a store that never changes, rather than a plain
 * `useState` flipped in a `useEffect`, specifically so there's no direct `setState` call inside
 * an effect body (flagged by the react-hooks/set-state-in-effect lint rule) — React still
 * reruns `useSyncExternalStore`'s snapshot check once after hydration commits, so the "flip to
 * true" timing is identical either way. */
export function useHasHydrated(): boolean {
  return useSyncExternalStore(subscribeNever, getHydratedTrue, getHydratedFalseOnServer);
}
